import React, { useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import {
  MessageSquare,
  Send,
  X,
  Search,
  Users,
  User,
  Building2,
  PhoneCall,
  Loader2,
  CheckCheck,
  BedDouble,
  Plus,
  Sparkles,
  ChevronRight,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { useStaffChatSession } from "@/hooks/chat/useStaffChatSession.js";
import { cn } from "@/lib/utils";

const QUICK_STAFF_TEMPLATES = [
  "Hello! How may I assist you today?",
  "Our team has received your request and is on the way.",
  "Your request has been completed. Please let us know if you need anything else!",
  "Thank you for reaching out. We are checking on this right away.",
];

const formatChatTime = (dateVal) => {
  if (!dateVal) return "";
  let d;
  if (typeof dateVal === "string") {
    if (!dateVal.endsWith("Z") && !dateVal.includes("+") && !/-\d{2}:\d{2}$/.test(dateVal)) {
      d = new Date(dateVal + "Z");
    } else {
      d = new Date(dateVal);
    }
  } else if (dateVal instanceof Date) {
    d = dateVal;
  } else {
    d = new Date(dateVal);
  }
  if (isNaN(d.getTime())) return "";
  return d.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
};

const StaffChatDrawer = ({
  isOpen,
  onClose,
  authContext,
  initialTargetUser = null,
  initialDepartment = null,
  initialGuestSession = null,
  onStartCall = null,
}) => {
  const currentUserId = authContext?.userId || authContext?.user?.id;
  const currentUserName = authContext?.userName || authContext?.user?.name || authContext?.user?.fullName || "Staff";

  const {
    conversations,
    activeConversation,
    messages,
    loading,
    sending,
    otherUserTyping,
    directory,
    startGuestChat,
    startStaffChat,
    startDepartmentChat,
    selectConversation,
    sendMessage,
    sendTyping,
    loadInitialData,
  } = useStaffChatSession({
    authContext,
  });

  const [inputVal, setInputVal] = useState("");
  const [searchQuery, setSearchQuery] = useState("");
  const [activeTab, setActiveTab] = useState("CHATS"); // CHATS | GUESTS | STAFF | DEPTS
  const messagesEndRef = useRef(null);

  const selectedTargetKey = useMemo(() => {
    if (initialGuestSession?.sessionId) return `guest-${initialGuestSession.sessionId}`;
    if (initialTargetUser?.userId) return `staff-${initialTargetUser.userId}`;
    if (initialDepartment) return `dept-${initialDepartment}`;
    return null;
  }, [initialGuestSession?.sessionId, initialTargetUser?.userId, initialDepartment]);

  const handledTargetKeyRef = useRef(null);

  useEffect(() => {
    if (!isOpen) {
      handledTargetKeyRef.current = null;
      return;
    }
    if (selectedTargetKey && handledTargetKeyRef.current !== selectedTargetKey) {
      handledTargetKeyRef.current = selectedTargetKey;
      if (initialGuestSession?.sessionId) {
        startGuestChat(initialGuestSession);
      } else if (initialTargetUser?.userId) {
        startStaffChat(initialTargetUser);
      } else if (initialDepartment) {
        startDepartmentChat(initialDepartment);
      }
    }
  }, [
    isOpen,
    selectedTargetKey,
    initialGuestSession,
    initialTargetUser,
    initialDepartment,
    startGuestChat,
    startStaffChat,
    startDepartmentChat,
  ]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages, otherUserTyping]);

  const handleSend = async (e) => {
    e?.preventDefault();
    if (!inputVal.trim() || sending) return;
    const text = inputVal;
    setInputVal("");
    sendTyping(false);

    await sendMessage({
      content: text,
      recipientUserId: initialTargetUser?.userId,
      targetDepartment: initialDepartment,
    });
  };

  const handleInputChange = (e) => {
    setInputVal(e.target.value);
    sendTyping(e.target.value.length > 0);
  };

  const handleTemplateClick = async (tmpl) => {
    await sendMessage({
      content: tmpl,
      recipientUserId: initialTargetUser?.userId,
      targetDepartment: initialDepartment,
    });
  };

  const list = Array.isArray(conversations) ? conversations : [];
  const filteredConversations = useMemo(() => {
    if (!isOpen) return [];
    return list.filter((c) => {
      if (!c) return false;
      if (searchQuery.trim()) {
        const q = searchQuery.toLowerCase();
        const title = (c.guestName || c.otherParticipantName || c.roomName || c.department || "").toLowerCase();
        const snippet = (c.lastMessageSnippet || "").toLowerCase();
        return title.includes(q) || snippet.includes(q);
      }
      return true;
    });
  }, [list, searchQuery, isOpen]);

  const filteredGuests = useMemo(() => {
    const guests = directory.activeGuests || [];
    if (!searchQuery.trim()) return guests;
    const q = searchQuery.toLowerCase();
    return guests.filter(
      (g) =>
        (g.guestName || "").toLowerCase().includes(q) ||
        (g.roomName || "").toLowerCase().includes(q)
    );
  }, [directory.activeGuests, searchQuery]);

  const filteredStaff = useMemo(() => {
    const staff = directory.staff || [];
    if (!searchQuery.trim()) return staff;
    const q = searchQuery.toLowerCase();
    return staff.filter(
      (s) =>
        (s.displayName || "").toLowerCase().includes(q) ||
        (s.department || "").toLowerCase().includes(q)
    );
  }, [directory.staff, searchQuery]);

  const filteredDepartments = useMemo(() => {
    const depts = directory.departments || [];
    if (!searchQuery.trim()) return depts;
    const q = searchQuery.toLowerCase();
    return depts.filter((d) =>
      (d.departmentName || d.departmentKey || "").toLowerCase().includes(q)
    );
  }, [directory.departments, searchQuery]);

  if (!isOpen) return null;

  const drawerContent = (
    <div className="fixed inset-0 z-[9999] flex justify-end bg-slate-900/50 backdrop-blur-xs transition-all duration-300 animate-in fade-in">
      <div className="absolute inset-0" onClick={onClose} />

      <div className="relative flex h-full w-full max-w-2xl flex-col border-l border-slate-200 bg-white shadow-2xl transition-transform duration-300 animate-in slide-in-from-right sm:flex-row">
        {/* Left Sidebar: Conversations & Directory */}
        <div className="flex w-full sm:w-80 flex-col border-r border-slate-100 bg-slate-50/70 shrink-0">
          {/* Header */}
          <div className="flex items-center justify-between border-b border-slate-200/80 p-3.5 bg-white">
            <div className="flex items-center gap-2">
              <MessageSquare className="h-5 w-5 text-primary" />
              <h2 className="text-sm font-bold text-slate-900">Hotel Messages</h2>
            </div>
            <button
              type="button"
              onClick={onClose}
              className="sm:hidden rounded-lg p-1.5 text-slate-400 hover:bg-slate-100"
            >
              <X className="h-4 w-4" />
            </button>
          </div>

          {/* Search & Navigation Tabs */}
          <div className="p-2.5 space-y-2 border-b border-slate-200/60 bg-white">
            <div className="relative">
              <Search className="pointer-events-none absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-slate-400" />
              <Input
                type="text"
                placeholder="Search rooms, guests, staff..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="h-8 rounded-lg pl-8 text-xs bg-slate-50 border-slate-200"
              />
            </div>

            <div className="grid grid-cols-4 gap-1">
              <button
                type="button"
                onClick={() => setActiveTab("CHATS")}
                className={cn(
                  "rounded-md py-1 text-[11px] font-semibold transition-all text-center",
                  activeTab === "CHATS"
                    ? "bg-slate-900 text-white shadow-xs"
                    : "bg-transparent text-slate-600 hover:bg-slate-100"
                )}
              >
                Chats
              </button>
              <button
                type="button"
                onClick={() => setActiveTab("GUESTS")}
                className={cn(
                  "rounded-md py-1 text-[11px] font-semibold transition-all text-center",
                  activeTab === "GUESTS"
                    ? "bg-slate-900 text-white shadow-xs"
                    : "bg-transparent text-slate-600 hover:bg-slate-100"
                )}
              >
                Guests
              </button>
              <button
                type="button"
                onClick={() => setActiveTab("STAFF")}
                className={cn(
                  "rounded-md py-1 text-[11px] font-semibold transition-all text-center",
                  activeTab === "STAFF"
                    ? "bg-slate-900 text-white shadow-xs"
                    : "bg-transparent text-slate-600 hover:bg-slate-100"
                )}
              >
                Staff
              </button>
              <button
                type="button"
                onClick={() => setActiveTab("DEPTS")}
                className={cn(
                  "rounded-md py-1 text-[11px] font-semibold transition-all text-center",
                  activeTab === "DEPTS"
                    ? "bg-slate-900 text-white shadow-xs"
                    : "bg-transparent text-slate-600 hover:bg-slate-100"
                )}
              >
                Depts
              </button>
            </div>
          </div>

          {/* List Content */}
          <div className="flex-1 overflow-y-auto divide-y divide-slate-100">
            {/* TAB: RECENT CHATS */}
            {activeTab === "CHATS" && (
              <>
                {filteredConversations.length === 0 ? (
                  <div className="p-6 text-center text-xs text-slate-400">
                    <MessageSquare className="mx-auto h-8 w-8 text-slate-300 mb-2" />
                    <span>No conversations yet.</span>
                    <p className="mt-1 text-[11px] text-slate-400">
                      Select a Guest or Department above to start chatting.
                    </p>
                    <div className="mt-3 flex flex-col gap-1.5">
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => setActiveTab("GUESTS")}
                        className="rounded-xl text-xs font-semibold gap-1.5 w-full"
                      >
                        <User className="h-3.5 w-3.5 text-primary" />
                        <span>Message Guest</span>
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => setActiveTab("DEPTS")}
                        className="rounded-xl text-xs font-semibold gap-1.5 w-full"
                      >
                        <Building2 className="h-3.5 w-3.5 text-primary" />
                        <span>Message Department</span>
                      </Button>
                    </div>
                  </div>
                ) : (
                  filteredConversations.map((c) => {
                    const isSelected = activeConversation?.id === c.id || (c.guestSessionId && activeConversation?.guestSessionId === c.guestSessionId);
                    const isGuest = c.type !== "StaffToStaff";
                    const title = isGuest
                      ? c.roomName
                        ? `Room ${c.roomName}${c.guestName ? ` (${c.guestName})` : ""}`
                        : c.guestName || "Guest Concierge"
                      : c.otherParticipantName || c.title || "Staff Colleague";

                    const unread = c.staffUnreadCount || 0;
                    const time = formatChatTime(c.lastMessageAtUtc);

                    return (
                      <button
                        key={c.id}
                        type="button"
                        onClick={() => selectConversation(c)}
                        className={cn(
                          "w-full text-left p-3 transition-colors flex items-start gap-2.5",
                          isSelected
                            ? "bg-white shadow-xs border-l-3 border-primary"
                            : "hover:bg-white/80"
                        )}
                      >
                        <div
                          className={cn(
                            "flex h-9 w-9 shrink-0 items-center justify-center rounded-xl text-xs font-bold shadow-2xs",
                            isGuest
                              ? "bg-amber-100 text-amber-800"
                              : "bg-slate-900 text-white"
                          )}
                        >
                          {isGuest ? (
                            c.roomName || <BedDouble className="h-4 w-4" />
                          ) : (
                            c.otherParticipantName?.[0] || "S"
                          )}
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center justify-between">
                            <p className="text-xs font-bold text-slate-900 truncate">
                              {title}
                            </p>
                            <span className="text-[10px] text-slate-400 shrink-0">
                              {time}
                            </span>
                          </div>
                          <p className="text-[11px] text-slate-500 truncate mt-0.5">
                            {c.lastMessageSnippet || c.lastMessageContent || "No messages yet"}
                          </p>
                        </div>
                        {unread > 0 && (
                          <span className="flex h-4 min-w-[16px] items-center justify-center rounded-full bg-primary px-1 text-[10px] font-bold text-white shadow-xs">
                            {unread}
                          </span>
                        )}
                      </button>
                    );
                  })
                )}
              </>
            )}

            {/* TAB: ACTIVE GUESTS DIRECTORY */}
            {activeTab === "GUESTS" && (
              <>
                {filteredGuests.length === 0 ? (
                  <div className="p-6 text-center text-xs text-slate-400">
                    <BedDouble className="mx-auto h-8 w-8 text-slate-300 mb-2" />
                    <span>No active guest check-ins found</span>
                  </div>
                ) : (
                  filteredGuests.map((guest) => {
                    const isSelected = activeConversation?.guestSessionId === guest.sessionId;
                    return (
                      <button
                        key={guest.sessionId}
                        type="button"
                        onClick={() => startGuestChat(guest)}
                        className={cn(
                          "w-full text-left p-3 transition-colors flex items-center justify-between gap-2.5",
                          isSelected
                            ? "bg-white shadow-xs border-l-3 border-primary"
                            : "hover:bg-white/80"
                        )}
                      >
                        <div className="flex items-center gap-2.5 min-w-0">
                          <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-amber-100 text-xs font-bold text-amber-800 shadow-2xs">
                            {guest.roomName}
                          </div>
                          <div className="min-w-0">
                            <p className="text-xs font-bold text-slate-900 truncate">
                              {guest.guestName}
                            </p>
                            <p className="text-[10px] text-slate-400">
                              Room {guest.roomName} • In-house
                            </p>
                          </div>
                        </div>
                        <span className="rounded-lg bg-slate-100 px-2 py-1 text-[10px] font-semibold text-slate-700 hover:bg-slate-200">
                          Message
                        </span>
                      </button>
                    );
                  })
                )}
              </>
            )}

            {/* TAB: STAFF DIRECTORY */}
            {activeTab === "STAFF" && (
              <>
                {filteredStaff.length === 0 ? (
                  <div className="p-6 text-center text-xs text-slate-400">
                    <Users className="mx-auto h-8 w-8 text-slate-300 mb-2" />
                    <span>No staff members found</span>
                  </div>
                ) : (
                  filteredStaff.map((staff) => {
                    const isSelected = activeConversation?.otherParticipantUserId === staff.userId;
                    return (
                      <button
                        key={staff.userId}
                        type="button"
                        onClick={() => startStaffChat(staff)}
                        className={cn(
                          "w-full text-left p-3 transition-colors flex items-center justify-between gap-2.5",
                          isSelected
                            ? "bg-white shadow-xs border-l-3 border-primary"
                            : "hover:bg-white/80"
                        )}
                      >
                        <div className="flex items-center gap-2.5 min-w-0">
                          <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-slate-900 text-xs font-bold text-white shadow-2xs">
                            {(staff.displayName || staff.name)?.[0] || "S"}
                          </div>
                          <div className="min-w-0">
                            <p className="text-xs font-bold text-slate-900 truncate">
                              {staff.displayName || staff.name}
                            </p>
                            <p className="text-[10px] text-slate-400">
                              {staff.department || "Staff Member"}
                            </p>
                          </div>
                        </div>
                        <span className="rounded-lg bg-slate-100 px-2 py-1 text-[10px] font-semibold text-slate-700 hover:bg-slate-200">
                          Direct
                        </span>
                      </button>
                    );
                  })
                )}
              </>
            )}

            {/* TAB: DEPARTMENTS */}
            {activeTab === "DEPTS" && (
              <>
                {filteredDepartments.map((dept) => {
                  const isSelected =
                    activeConversation?.department === dept.departmentKey &&
                    activeConversation?.type !== "GuestToDepartment";
                  return (
                    <button
                      key={dept.departmentKey}
                      type="button"
                      onClick={() => startDepartmentChat(dept.departmentKey, dept.departmentName)}
                      className={cn(
                        "w-full text-left p-3 transition-colors flex items-center justify-between gap-2.5",
                        isSelected
                          ? "bg-white shadow-xs border-l-3 border-primary"
                          : "hover:bg-white/80"
                      )}
                    >
                      <div className="flex items-center gap-2.5 min-w-0">
                        <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-emerald-100 text-xs font-bold text-emerald-800 shadow-2xs">
                          <Building2 className="h-4 w-4" />
                        </div>
                        <div className="min-w-0">
                          <p className="text-xs font-bold text-slate-900 truncate">
                            {dept.departmentName}
                          </p>
                          <p className="text-[10px] text-slate-400">Department Channel</p>
                        </div>
                      </div>
                      <span className="rounded-lg bg-slate-100 px-2 py-1 text-[10px] font-semibold text-slate-700 hover:bg-slate-200">
                        Broadcast
                      </span>
                    </button>
                  );
                })}
              </>
            )}
          </div>
        </div>

        {/* Right Panel: Chat Area */}
        <div className="flex flex-1 flex-col bg-white">
          {/* Header */}
          <div className="flex items-center justify-between border-b border-slate-100 p-3.5 bg-white">
            {activeConversation ? (
              <div className="flex items-center justify-between w-full">
                <div className="flex items-center gap-2.5 min-w-0">
                  <div
                    className={cn(
                      "flex h-9 w-9 shrink-0 items-center justify-center rounded-xl text-xs font-bold shadow-2xs",
                      activeConversation.type !== "StaffToStaff"
                        ? "bg-amber-100 text-amber-800"
                        : "bg-slate-900 text-white"
                    )}
                  >
                    {activeConversation.type !== "StaffToStaff" ? (
                      activeConversation.roomName || <BedDouble className="h-4 w-4" />
                    ) : (
                      activeConversation.otherParticipantName?.[0] || "S"
                    )}
                  </div>
                  <div className="min-w-0">
                    <h3 className="text-xs sm:text-sm font-bold text-slate-900 truncate">
                      {activeConversation.type !== "StaffToStaff"
                        ? activeConversation.roomName
                          ? `Room ${activeConversation.roomName}${activeConversation.guestName ? ` (${activeConversation.guestName})` : ""}`
                          : activeConversation.guestName || "Guest Concierge"
                        : activeConversation.otherParticipantName || activeConversation.title || "Staff Member"}
                    </h3>
                    <p className="text-[10px] text-slate-400">
                      {activeConversation.type !== "StaffToStaff"
                        ? "Guest Messaging Channel"
                        : activeConversation.department || "Staff Direct"}
                    </p>
                  </div>
                </div>

                <div className="flex items-center gap-1.5">
                  {onStartCall && activeConversation.type !== "StaffToStaff" && activeConversation.guestSessionId && (
                    <Button
                      type="button"
                      size="sm"
                      variant="outline"
                      onClick={() =>
                        onStartCall({
                          type: "guest",
                          sessionId: activeConversation.guestSessionId,
                          roomName: activeConversation.roomName,
                          guestName: activeConversation.guestName,
                        })
                      }
                      className="h-8 gap-1 rounded-xl text-xs text-emerald-700 hover:bg-emerald-50 border-emerald-200"
                    >
                      <PhoneCall className="h-3.5 w-3.5" />
                      <span className="hidden sm:inline">Call Guest</span>
                    </Button>
                  )}
                  <button
                    type="button"
                    onClick={onClose}
                    className="rounded-lg p-1.5 text-slate-400 hover:bg-slate-100"
                  >
                    <X className="h-4 w-4" />
                  </button>
                </div>
              </div>
            ) : (
              <div className="flex items-center justify-between w-full">
                <span className="text-xs font-semibold text-slate-400">
                  Select a conversation
                </span>
                <button
                  type="button"
                  onClick={onClose}
                  className="rounded-lg p-1.5 text-slate-400 hover:bg-slate-100"
                >
                  <X className="h-4 w-4" />
                </button>
              </div>
            )}
          </div>

          {/* Quick Staff Templates Bar */}
          {activeConversation && (
            <div className="flex items-center gap-1.5 overflow-x-auto border-b border-slate-100 bg-slate-50/60 px-3 py-1.5 no-scrollbar">
              <span className="text-[10px] font-bold uppercase tracking-wider text-slate-400 shrink-0">
                Quick Reply:
              </span>
              {QUICK_STAFF_TEMPLATES.map((tmpl, idx) => (
                <button
                  key={idx}
                  type="button"
                  onClick={() => handleTemplateClick(tmpl)}
                  className="rounded-lg border border-slate-200 bg-white px-2 py-0.5 text-[11px] font-medium text-slate-600 shadow-2xs hover:bg-slate-100 active:scale-95 shrink-0"
                >
                  {tmpl}
                </button>
              ))}
            </div>
          )}

          {/* Messages Area */}
          <div className="flex-1 overflow-y-auto p-4 space-y-3 bg-slate-50/30 min-h-0">
            {!activeConversation ? (
              <div className="flex h-full flex-col items-center justify-center text-center p-6 text-slate-400">
                <MessageSquare className="h-12 w-12 text-slate-200 mb-3" />
                <p className="text-sm font-semibold text-slate-600">
                  No active conversation selected
                </p>
                <p className="text-xs text-slate-400 mt-1 max-w-xs">
                  Choose a guest room from the list, or select a department or colleague to begin chatting.
                </p>
              </div>
            ) : loading && messages.length === 0 ? (
              <div className="flex h-full items-center justify-center">
                <Loader2 className="h-6 w-6 animate-spin text-slate-400" />
              </div>
            ) : messages.length === 0 ? (
              <div className="flex h-full flex-col items-center justify-center text-center p-6 text-slate-400">
                <MessageSquare className="h-10 w-10 text-slate-200 mb-2" />
                <p className="text-xs font-semibold text-slate-600">
                  No messages yet in this conversation
                </p>
                <p className="text-[11px] text-slate-400 mt-1">
                  Type a greeting below to connect with the guest or team.
                </p>
              </div>
            ) : (
              messages.map((msg, index) => {
                const isMe =
                  msg.senderType === "Guest"
                    ? false
                    : activeConversation?.type === "StaffToStaff"
                    ? (currentUserId && msg.senderUserId
                        ? msg.senderUserId === currentUserId
                        : currentUserName && msg.senderDisplayName
                        ? msg.senderDisplayName.trim().toLowerCase() === currentUserName.trim().toLowerCase()
                        : activeConversation?.otherParticipantName
                        ? msg.senderDisplayName !== activeConversation.otherParticipantName
                        : true)
                    : true;
                const time = formatChatTime(msg.createdAt);

                return (
                  <div
                    key={msg.id || index}
                    className={cn(
                      "flex flex-col",
                      isMe ? "items-end" : "items-start"
                    )}
                  >
                    <div
                      className={cn(
                        "rounded-2xl px-3.5 py-2 text-xs sm:text-sm max-w-[80%] shadow-2xs",
                        isMe
                          ? "bg-slate-900 text-white rounded-br-none"
                          : "bg-white text-slate-800 border border-slate-200/80 rounded-bl-none"
                      )}
                    >
                      {!isMe && (
                        <p className="text-[10px] font-bold text-amber-600 mb-0.5">
                          {msg.senderDisplayName || "Guest"}
                        </p>
                      )}
                      <p className="whitespace-pre-wrap">{msg.content}</p>
                      <div
                        className={cn(
                          "mt-1 flex items-center justify-end gap-1 text-[9px]",
                          isMe ? "text-slate-400" : "text-slate-400"
                        )}
                      >
                        <span>{time}</span>
                        {isMe && (
                          <CheckCheck
                            className={cn(
                              "h-3 w-3",
                              msg.isRead ? "text-sky-400" : "text-slate-400"
                            )}
                          />
                        )}
                      </div>
                    </div>
                  </div>
                );
              })
            )}

            {otherUserTyping && (
              <div className="flex items-center gap-1 text-xs text-slate-400 italic">
                <span className="h-1.5 w-1.5 rounded-full bg-amber-500 animate-ping" />
                <span>{otherUserTyping} is typing...</span>
              </div>
            )}

            <div ref={messagesEndRef} />
          </div>

          {/* Input Footer */}
          {activeConversation && (
            <form
              onSubmit={handleSend}
              className="border-t border-slate-100 p-3 bg-white"
            >
              <div className="flex items-center gap-2">
                <Input
                  type="text"
                  placeholder="Type a message or use quick reply..."
                  value={inputVal}
                  onChange={handleInputChange}
                  className="flex-1 rounded-xl text-xs sm:text-sm"
                />
                <Button
                  type="submit"
                  size="sm"
                  disabled={!inputVal.trim() || sending}
                  className="h-9 w-9 shrink-0 rounded-xl bg-slate-900 text-white hover:bg-slate-800"
                >
                  {sending ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    <Send className="h-4 w-4" />
                  )}
                </Button>
              </div>
            </form>
          )}
        </div>
      </div>
    </div>
  );

  return typeof document !== "undefined" ? createPortal(drawerContent, document.body) : drawerContent;
};

export default StaffChatDrawer;
